import {Dialog, IconButton, TimeAgo} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {type ReactNode, useState} from 'react';

import type {SlurmLatestCommandQuery} from '../../../generated/graphql';
import {useQuery} from '../../../state/graphql/apolloWithAbort';
import {useNavbarBottom} from '../../../util/hooks/useNavbarBottom';
import {SlurmCommandStatusIndicator} from '../SlurmCommandStatusIndicator';
import {
  type SlurmConfirmVariant,
  SlurmJobConfirmModal,
} from '../SlurmJobConfirmModal';
import {SLURM_LATEST_COMMAND_QUERY} from '../slurmJobQueries.query';
import {ViewInGrafanaButton} from '../ViewInGrafanaButton';
import {MetadataRow, type SlurmJobMetadata} from './shared';

const EMPTY_METADATA: SlurmJobMetadata = {};

function SectionTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={`mb-12 px-16 text-sm font-semibold uppercase tracking-wide text-moon-500 dark:text-wb-text-tertiary${
        className ? ` ${className}` : ''
      }`}>
      {children}
    </h3>
  );
}

function renderMetadataTime(value?: string) {
  if (value == null) {
    return undefined;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return undefined;
  }
  return <TimeAgo dateTime={value} />;
}

interface SlurmJobDrawerProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  entityName: string;
  projectName: string;
  runName: string;
  clusterName?: string;
  region?: string;
  isRunLive?: boolean;
  readOnly?: boolean;
  grafanaUrl?: string;
  metadata?: SlurmJobMetadata;
}

export function SlurmJobDrawer({
  open,
  onClose,
  jobId,
  entityName,
  projectName,
  runName,
  clusterName,
  region,
  isRunLive = false,
  readOnly = false,
  grafanaUrl,
  metadata = EMPTY_METADATA,
}: SlurmJobDrawerProps) {
  const [confirmVariant, setConfirmVariant] =
    useState<SlurmConfirmVariant | null>(null);
  const canCancelJob = isRunLive && !readOnly;
  const canRequeueJob = !readOnly;
  const offsetTop = useNavbarBottom();
  const {data: latestCommandData, refetch: refetchLatestCommand} =
    useQuery<SlurmLatestCommandQuery>(SLURM_LATEST_COMMAND_QUERY, {
      variables: {entityName, projectName, runName},
      skip: !open || !runName,
      fetchPolicy: 'cache-and-network',
    });
  const latestCommand =
    latestCommandData?.project?.run?.slurmControl?.latestCommand ?? null;

  const jobDetailRows: Array<{label: string; value?: ReactNode}> = [
    {label: 'Job ID', value: jobId},
    {label: 'Cluster', value: clusterName},
    {label: 'Region', value: region},
    {label: 'Job name', value: metadata.name},
    {label: 'User', value: metadata.user},
    {label: 'Account', value: metadata.account},
    {label: 'Partition', value: metadata.partition},
    {label: 'QoS', value: metadata.qos},
    {label: 'Nodes', value: metadata.numNodes},
    {label: 'Node list', value: metadata.nodelist},
    {label: 'Submit directory', value: metadata.submitDir},
    {label: 'CPUs per task', value: metadata.cpusPerTask},
    {label: 'Memory per CPU', value: metadata.memPerCpu},
  ];

  return (
    <>
      <SlurmJobConfirmModal
        isOpen={confirmVariant != null}
        variant={confirmVariant ?? 'cancel'}
        jobId={jobId}
        entityName={entityName}
        projectName={projectName}
        runName={runName}
        isRunLive={isRunLive}
        onClose={() => setConfirmVariant(null)}
        onSuccess={() => {
          void refetchLatestCommand?.();
        }}
      />
      <Dialog.Drawer
        isOpen={open && confirmVariant == null}
        onOpenChange={isOpen => !isOpen && onClose()}
        backdrop={false}
        offsetTop={offsetTop}
        aria-label={`Slurm job ${jobId}`}>
        <TailwindContents>
          <div className="flex shrink-0 items-center justify-between gap-8 border-b border-wb-border px-20 py-16">
            <div className="flex min-w-0 items-center gap-16 overflow-hidden">
              <span className="text-xl text-wb-text-primary dark:text-moon-200">
                Slurm job {jobId}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-8">
              {canCancelJob && (
                <IconButton
                  icon="do-not-prohibited"
                  variant="ghost"
                  size="md"
                  tooltip={{children: 'Cancel Slurm job'}}
                  onPress={() => setConfirmVariant('cancel')}
                />
              )}
              {canRequeueJob && (
                <IconButton
                  icon="reset"
                  variant="ghost"
                  size="md"
                  tooltip={{children: 'Requeue Slurm job'}}
                  onPress={() => setConfirmVariant('requeue')}
                />
              )}
              {grafanaUrl && (
                <ViewInGrafanaButton
                  grafanaUrl={grafanaUrl}
                  variant="ghost"
                  size="md"
                  iconOnly
                />
              )}
              <IconButton
                icon="close"
                variant="ghost"
                aria-label="Close"
                onPress={onClose}
              />
            </div>
          </div>
          <Dialog.Content>
            <div className="py-16">
              {latestCommand && (
                <div className="mb-20">
                  <SectionTitle>Command status</SectionTitle>
                  <MetadataRow
                    label="Status"
                    value={
                      <SlurmCommandStatusIndicator command={latestCommand} />
                    }
                  />
                  <MetadataRow
                    label="Last updated"
                    value={renderMetadataTime(latestCommand.updatedAt)}
                  />
                  {latestCommand.errorMessage && (
                    <MetadataRow
                      label="Error"
                      value={
                        <span className="text-red-600">
                          {latestCommand.errorMessage}
                        </span>
                      }
                    />
                  )}
                </div>
              )}
              <SectionTitle className="mt-4">Job details</SectionTitle>
              {jobDetailRows.map(row => (
                <MetadataRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </div>
          </Dialog.Content>
        </TailwindContents>
      </Dialog.Drawer>
    </>
  );
}
